/**
 * Utility functions for date formatting with internationalization support
 */

/**
 * Maps language codes to locale strings for date formatting
 */
const getLocaleFromLanguage = (language: string): string => {
	switch (language) {
		case 'en':
			return 'en-US';
		case 'es':
		default:
			return 'es-ES';
	}
};

/**
 * Formats a date string using the user's language preference
 * @param dateString - The date string to format
 * @param language - The user's language code (e.g., 'es', 'en')
 * @param options - Additional options for toLocaleDateString
 * @returns Formatted date string
 */
export const formatDateWithLocale = (
	dateString: string,
	language: string = 'es',
	options?: Intl.DateTimeFormatOptions
): string => {
	if (!dateString) return '-';

	const date = new Date(dateString);
	const locale = getLocaleFromLanguage(language);

	return date.toLocaleDateString(locale, options);
};

/**
 * Formats a date string for display in tables (short format)
 * @param dateString - The date string to format
 * @param language - The user's language code
 * @returns Formatted date string
 */
export const formatTableDate = (dateString: string, language: string = 'es'): string => {
	return formatDateWithLocale(dateString, language);
};

/**
 * Formats a date string to show month and year
 * @param dateString - The date string to format
 * @param language - The user's language code
 * @returns Formatted month and year string
 */
export const formatMonthYear = (dateString: string, language: string = 'es'): string => {
	return formatDateWithLocale(dateString, language, {
		month: 'long',
		year: 'numeric'
	});
};

/**
 * Converts ISO date (YYYY-MM-DD) to Spanish format (DD/MM/YYYY)
 * @param iso - Date in YYYY-MM-DD format
 * @returns Date in DD/MM/YYYY format
 */
export const isoToSpanish = (iso: string): string => {
	if (!iso) return '';
	const [y, m, d] = iso.split('-');
	return `${d}/${m}/${y}`;
};

/**
 * Converts Spanish format (DD/MM/YYYY) to ISO date (YYYY-MM-DD)
 * @param spanish - Date in DD/MM/YYYY format
 * @returns Date in YYYY-MM-DD format
 */
export const spanishToIso = (spanish: string): string => {
	if (!spanish) return '';
	const [d, m, y] = spanish.split('/');
	return `${y}-${m}-${d}`;
};

/**
 * Calculates the next contribution date based on the contribution day.
 * If today is before or on the contribution day, returns this month's date.
 * Otherwise returns next month's date.
 * @param contributionDay - Day of the month for contributions (default 20)
 * @param now - Reference date (default: today)
 * @returns The next contribution Date object
 */
export const getNextContributionDate = (
	contributionDay: number = 20,
	now: Date = new Date()
): Date => {
	return now.getDate() <= contributionDay
		? new Date(now.getFullYear(), now.getMonth(), contributionDay)
		: new Date(now.getFullYear(), now.getMonth() + 1, contributionDay);
};
